import { RootState } from "@/src/app/store";
import {
  addCaseDocument,
  deleteCaseDocument,
  fetchCaseDocuments,
  fetchDocumentDownloadUrl,
  replaceCaseDocument,
} from "../../../../src/thunks/caseDocument";
import { CaseDocument } from "../../../api/caseDocuments";
import {
  createSlice,
  createEntityAdapter,
  EntityState,
} from "@reduxjs/toolkit";

const caseDocumentsAdapter = createEntityAdapter<CaseDocument, string>({
  selectId: (document) => document.document_id,
});

export const DEFAULT_DOCUMENT_FLAGS = {
  deleting: false,
  deleteError: null,
  downloading: false,
  downloadError: null,
  replacing: false,
  replaceError: null,
};

export const buildCaseDocument = (
  requestPayload: any,
  responsePayload: any
): CaseDocument => ({
  document_id: responsePayload.document_id,
  document_type: requestPayload.document_type,
  entity_type: requestPayload.entity_type,
  entity_id: requestPayload.entity_id,
  file_type: requestPayload.file_type,
  file_name: requestPayload.file_name,
  file_reference: responsePayload.file_reference,
  created_at: responsePayload.created_at,
  status: responsePayload.status,
  notes: requestPayload.notes,

  ...DEFAULT_DOCUMENT_FLAGS,
});

type DocumentFlags = Pick<
  CaseDocument,
  | "deleting"
  | "deleteError"
  | "downloading"
  | "downloadError"
  | "replacing"
  | "replaceError"
>;

const setDocumentFlags = (
  state: CaseDocumentsState,
  documentId: string,
  changes: Partial<DocumentFlags>
): void => {
  const document = state.documents.entities[documentId];

  if (!document) return;

  Object.assign(document, changes);
};

export interface CaseDocumentsState {
  documents: EntityState<CaseDocument, string>;

  loading: boolean;
  error: string | null;
  lastFetched?: number;

  uploading: boolean;
  uploadError: string | null;
  caseId: string | null;
}

const initialState: CaseDocumentsState = {
  documents: caseDocumentsAdapter.getInitialState(),
  loading: false,
  error: null,
  lastFetched: undefined,
  uploading: false,
  uploadError: null,
  caseId: null,
};

const caseDocumentsSlice = createSlice({
  name: "caseDocuments",
  initialState,
  reducers: {
     resetCaseDocuments: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaseDocuments.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.caseId = action.meta.arg.case_id;
      })

      .addCase(fetchCaseDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;

        const documents = action.payload.documents.map((document) => ({
          ...document,
          ...DEFAULT_DOCUMENT_FLAGS,
        }));

        caseDocumentsAdapter.setAll(state.documents, documents);
        state.lastFetched = Date.now();
        state.caseId = action.meta.arg.case_id;
      })

      .addCase(fetchCaseDocuments.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch documents";
        state.caseId = null;
      })

      .addCase(addCaseDocument.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
      })

      .addCase(addCaseDocument.fulfilled, (state, action) => {
        state.uploading = false;
        state.uploadError = null;

        const requestPayload = action.meta.arg.payload;
        const responsePayload = action.payload;

        const newDocument: CaseDocument = buildCaseDocument(
          requestPayload,
          responsePayload
        );

        caseDocumentsAdapter.addOne(state.documents, newDocument);
      })

      .addCase(addCaseDocument.rejected, (state) => {
        state.uploading = false;
        state.uploadError = "Failed to upload document";
      })

      .addCase(replaceCaseDocument.pending, (state, action) => {
        setDocumentFlags(state, action.meta.arg.document_id, {
          replacing: true,
          replaceError: null,
        });
      })

      .addCase(replaceCaseDocument.fulfilled, (state, action) => {
        const oldDocumentId = action.meta.arg.document_id;
        const requestPayload = action.meta.arg.payload;
        const responsePayload = action.payload;

        caseDocumentsAdapter.removeOne(state.documents, oldDocumentId);

        const newDocument: CaseDocument = buildCaseDocument(
          requestPayload,
          responsePayload
        );

        caseDocumentsAdapter.addOne(state.documents, newDocument);
      })

      .addCase(replaceCaseDocument.rejected, (state, action) => {
        setDocumentFlags(state, action.meta.arg.document_id, {
          replacing: false,
          replaceError: "Failed to replace document",
        });
      })

      .addCase(deleteCaseDocument.pending, (state, action) => {
        setDocumentFlags(state, action.meta.arg.document_id, {
          deleting: true,
          deleteError: null,
        });
      })

      .addCase(deleteCaseDocument.fulfilled, (state, action) => {
        const documentId = action.meta.arg.document_id;

        caseDocumentsAdapter.removeOne(state.documents, documentId);
      })

      .addCase(deleteCaseDocument.rejected, (state, action) => {
        setDocumentFlags(state, action.meta.arg.document_id, {
          deleting: false,
          deleteError: "Failed to delete document",
        });
      })

      .addCase(fetchDocumentDownloadUrl.pending, (state, action) => {
        setDocumentFlags(state, action.meta.arg.document_id, {
          downloading: true,
          downloadError: null,
        });
      })

      .addCase(fetchDocumentDownloadUrl.fulfilled, (state, action) => {
        setDocumentFlags(state, action.meta.arg.document_id, {
          downloading: false,
          downloadError: null,
        });
      })

      .addCase(fetchDocumentDownloadUrl.rejected, (state, action) => {
        setDocumentFlags(state, action.meta.arg.document_id, {
          downloading: false,
          downloadError: "Failed to download document",
        });
      });
  },
});

export const isCaseDocumentsLoading = (state: RootState) => state.caseDocuments.loading;

export const { resetCaseDocuments } = caseDocumentsSlice.actions;
export const caseDocumentsReducer = caseDocumentsSlice.reducer;
export { caseDocumentsAdapter };